import React, { useState } from "react";
import { Tooltip } from "@mui/material";
import { PAGES_LIST } from "../../Helpers/constants";
import { Page } from "../../Helpers/interfaces";
import { useLocation, useNavigate } from "react-router-dom";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import { userLogout } from "../../Helpers/utils";
import { ReactComponent as AriaiLogo } from "../../images/svg/icons/ariai_logo.svg";

interface SideBarProps {
  selectedPage: number | string; // Adjust type as per your application's logic
  setSelectedPage: React.Dispatch<React.SetStateAction<number>>; // Adjust type as per your application's logic
}

const SideBar: React.FC<SideBarProps> = ({ selectedPage, setSelectedPage }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const location = useLocation();
  return (
    <div className="flex flex-col justify-start min-w-full max-h-screen p-6 md:pb-6 pb-0 md:gap-5 gap-1">
      <div className="md:flex flex-col max-w-max gap-0 mb-3 hidden">
        <div className="font-inter font-thin text-4xl leading-6 tracking-tighter">
          Traders Forum
        </div>
        <div className="w-full flex justify-end">
          <div className="font-inter font-thin text-xs leading-4 tracking-tighter text-black mt-2">
            supported by{" "}
            <a
              href="https://www.anthillfunds.in/"
              target="_blank"
              rel="noreferrer"
            >
              Anthill Funds
            </a>
          </div>
        </div>
      </div>

      <div
        className="flex flex-row justify-between items-center w-full md:hidden"
        onClick={toggleSidebar}
      >
        <div className="inline-flex gap-2 items-center justify-start">
          <AriaiLogo
            className={`md:hidden p-1 border-2 border-slate-200 rounded-lg transition-all duration-300 ${
              isOpen ? "size-12" : "size-8"
            }`}
          />
          <div
            className={`font-inter md:text-2xl transition-all duration-300 ${
              isOpen ? "text-xl font-semibold" : "text-2xl font-thin"
            } tracking-tighter`}
          >
            Traders Forum
          </div>
        </div>

        <div className="md:hidden flex">
          <button
            onClick={toggleSidebar}
            className={`text-gray-700 ${
              !isOpen && "bg-gray-200"
            } px-4 py-2 rounded-md transition-transform duration-300`}
          >
            <svg
              className={`w-6 h-6 transition-transform duration-300 ${
                isOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Sidebar content with transition */}
      <div
        className={`${
          isOpen ? "max-h-[1000px]" : "max-h-0"
        } overflow-hidden md:max-h-none transition-max-height duration-500 ease-in-out md:flex md:flex-col md:justify-center md:gap-5`}
      >
        {PAGES_LIST.map((page: Page) => (
          <Tooltip
            key={page.id}
            title={page.disabled ? "This page is not available" : ""}
            placement="bottom"
            className="bg-slate-100"
            arrow
            disableHoverListener={!page.disabled}
            disableFocusListener={!page.disabled}
            disableTouchListener={!page.disabled}
          >
            <div
              className={`flex flex-row items-center px-4 gap-4 rounded-md 
                cursor-pointer min-h-10 min-w-min hover:bg-slate-100
                transition-all duration-300 hover:text-gray-900 ${[
                  location.pathname === page.toURL &&
                    "bg-slate-200 text-gray-800",
                  location.pathname !== page.toURL && "bg-white text-gray-500",
                  page.disabled &&
                    "hover:text-gray-300 text-gray-400 bg-slate-50 hover:bg-slate-50 cursor-not-allowed",
                ]
                  .filter(Boolean)
                  .join(" ")}`}
              onClick={() => {
                if (!page.disabled) {
                  setSelectedPage(page.id);
                  navigate(page.toURL);
                  toggleSidebar();
                }
              }}
            >
              {page.id === selectedPage ? <page.selectedIcon /> : <page.icon />}
              <div className="font-inter font-medium text-sm leading-6">
                {page.name}
              </div>
            </div>
          </Tooltip>
        ))}
        <div
          className={`md:hidden flex flex-row items-center px-4 gap-4 rounded-md 
                cursor-pointer min-h-10 min-w-min hover:bg-slate-100
                transition-all duration-300 hover:text-gray-900`}
          onClick={() => {
            setSelectedPage(-1);
            navigate("/newsupdates");
            toggleSidebar();
          }}
        >
          <NewspaperIcon />
          <div className="font-inter font-medium text-sm leading-6">
            News Sentiments
          </div>
        </div>
        <div
          className={`md:hidden flex flex-row items-center px-4 gap-4 rounded-md 
                cursor-pointer min-h-10 min-w-min hover:bg-slate-100
                transition-all duration-300 hover:text-gray-900`}
          onClick={() => {
            userLogout();
            navigate("/jumpin");
            toggleSidebar();
          }}
        >
          <ExitToAppIcon />
          <div className="font-inter font-medium text-sm leading-6">Logout</div>
        </div>
      </div>
    </div>
  );
};

export default SideBar;
